import type { History } from "history";

type Prompt = Parameters<History["block"]>[0];

// history v4 has one place for a blocker. A second block() takes it, so the first is
// never asked again, and releasing either one empties it. A <Prompt> about unsaved
// changes and the hold for a new version (new-version.ts) can both be up: the hold
// would reload the page over the unsaved text without a word, and a Prompt coming and
// going would silently end the hold. Here they share the place.
//
// A message asks the person, so it goes first, and a "yes" lets that change of page
// through. The hold is still there for the next one.
export function shareHistoryBlocks(history: History) {
  const block = history.block.bind(history);
  const prompts: Prompt[] = [];
  let releaseBlock: (() => void) | undefined;

  const asked = () => [
    ...prompts.filter((prompt) => typeof prompt === "string"),
    ...prompts.filter((prompt) => typeof prompt !== "string"),
  ];

  history.block = (prompt = false) => {
    prompts.push(prompt);
    releaseBlock ??= block((location, action) => {
      for (const current of asked()) {
        const answer =
          typeof current === "function" ? current(location, action) : current;
        if (answer !== undefined && answer !== true) {
          return answer;
        }
      }
      return undefined;
    });

    let released = false;
    return () => {
      if (released) {
        return;
      }
      released = true;
      prompts.splice(prompts.indexOf(prompt), 1);
      if (prompts.length === 0) {
        releaseBlock?.();
        releaseBlock = undefined;
      }
    };
  };
}
